import React, { useEffect, useState } from "react";
import { Modal } from "Components/Modal";
import { UtmEntry, Container as UtmEntryContainer } from "./UtmEntry";
import { useMultiDayEventContext } from "contexts/MultiDayEventContext";
import styled from "styled-components";
import { UtmDocument } from "@max/common/src/analytics";
import { ReactComponent as PlusIcon } from "assets/svg/plus.svg";
import { Body1 } from "melodies-source/Text";
import { deleteField, setDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { UtmEmptyState } from "./UtmEmptyState";
import { useMobileMediaQuery } from "hooks";
import { Divider } from "Components";

export interface Entry {
  key: string;
  label: string;
}

const NEW_ENTRY_KEY = "TEMP_KEY";

const buildUtmEntries = (utm: UtmDocument) =>
  Object.keys(utm.utmSources || {}).reduce(
    (map, key) => ({ ...map, [key]: { key, label: utm?.labels?.[key] || "" } }),
    {},
  );

type Props = React.ComponentProps<typeof Modal>;
type UtmEntryProps = React.ComponentProps<typeof UtmEntry>;
type UtmEntries = Record<string, Entry>;
type EntriesErrors = Record<string, Partial<Entry>>;

export const UtmSourceEditorModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { utm, utmDocReference } = useMultiDayEventContext();
  const [utmEntries, setUtmEntries] = useState<UtmEntries>({});
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [entriesErrors, setEntriesErrors] = useState<EntriesErrors>({});
  const isMobile = useMobileMediaQuery();
  const isEmptyState = Object.keys(utmEntries).length === 0;

  const handleAdd = () => {
    setUtmEntries((entries) => {
      if (!validateUtmEntries(utmEntries)) return entries;

      let newEntries = { ...entries };
      const prevoiusNewEntry = entries[NEW_ENTRY_KEY];
      if (prevoiusNewEntry) {
        newEntries = { ...entries, [prevoiusNewEntry.key]: prevoiusNewEntry };
        delete newEntries[NEW_ENTRY_KEY];
      }
      return {
        ...newEntries,
        [NEW_ENTRY_KEY]: { key: "", label: "" },
      };
    });
  };

  const handleChange: UtmEntryProps["onChange"] = (
    initialKey,
    property,
    value,
  ) => {
    setUtmEntries((entries) => {
      const newEntries = {
        ...entries,
        [initialKey]: { ...entries[initialKey], [property]: value },
      };
      if (Object.keys(entriesErrors).length > 0) validateUtmEntries(newEntries);
      return newEntries;
    });
  };

  const toggleDeletModalOpen = () => setDeleteModalIsOpen((open) => !open);

  const handleDeleteModalOpen: UtmEntryProps["onDelete"] = (key: string) => {
    setKeyToDelete(key);
    toggleDeletModalOpen();
  };

  const handleDelete = () => {
    setUtmEntries(({ [keyToDelete]: _, ...entries }) => {
      if (wasSubmitted) validateUtmEntries(entries);
      return entries;
    });
    toggleDeletModalOpen();
  };

  const validateUtmEntries = (entries: UtmEntries) => {
    const errors: EntriesErrors = {};
    const keyFrecuency = Object.values(entries).reduce((map, { key }) => {
      map[key] = (map[key] || 0) + 1;
      return map;
    }, {});

    for (const [initialKey, { key, label }] of Object.entries(entries)) {
      if (keyFrecuency[key] > 1) {
        errors[initialKey] = {
          ...errors[initialKey],
          key: "UTM strings must be unique",
        };
      }

      if (key === "") {
        errors[initialKey] = {
          ...errors[initialKey],
          key: "Must provide UTM string",
        };
      }

      if (initialKey === NEW_ENTRY_KEY && label === "") {
        errors[initialKey] = {
          ...errors[initialKey],
          label: "Must provide UTM reporting label",
        };
      }
    }
    setEntriesErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!wasSubmitted) setWasSubmitted(true);
    if (!validateUtmEntries(utmEntries)) return;

    const labels = {
      // add new utm strings
      ...Object.values(utmEntries)
        .filter((entry) => entry.label)
        .reduce((map, entry) => {
          map[entry.key] = entry.label.trim();
          return map;
        }, {}),
      // remove utm strings that were deleted
      ...Object.keys(utm.utmSources)
        .filter((key) => !utmEntries[key])
        .reduce((map, key) => {
          map[key] = deleteField();
          return map;
        }, {}),
      // remove utm strings where key changed
      ...Object.entries(utmEntries)
        .filter(([initialKey, entry]) => initialKey !== entry.key)
        .reduce((map, [initialKey]) => {
          map[initialKey] = deleteField();
          return map;
        }, {}),
    };

    const utmSources = {
      // add new utm strings
      ...Object.values(utmEntries)
        .filter((entry) => utm.utmSources[entry.key] === undefined)
        .reduce((map, entry) => {
          map[entry.key] = 0;
          return map;
        }, {}),
      // remove utm strings that were deleted
      ...Object.keys(utm.utmSources)
        .filter((key) => !utmEntries[key])
        .reduce((map, key) => {
          map[key] = deleteField();
          return map;
        }, {}),
      // remove utm strings where key changed
      ...Object.entries(utmEntries)
        .filter(([initialKey, entry]) => initialKey !== entry.key)
        .reduce((map, [initialKey]) => {
          map[initialKey] = deleteField();
          return map;
        }, {}),
    };

    const data: Partial<UtmDocument> = {};
    if (Object.keys(labels).length > 0) {
      data.labels = labels;
    }
    if (Object.keys(utmSources).length > 0) {
      data.utmSources = utmSources;
    }

    setDoc(utmDocReference, data, { merge: true });
    onClose();
    toast.success("UTM Source saved successfully");
  };

  useEffect(() => {
    if (utm?.utmSources) setUtmEntries(buildUtmEntries(utm));
  }, [Object.keys(utm?.utmSources || {}).length]);

  return (
    <>
      <Modal
        header="UTM Source Editor"
        isOpen={isOpen}
        onClose={onClose}
        variant="medium"
        actions={
          !isEmptyState && {
            main: {
              text: "Save",
              onClick: handleSave,
              disabled: Object.keys(entriesErrors).length > 0,
            },
            secondary: {
              text: "Cancel",
              onClick: onClose,
            },
            tertiary: {
              text: "Add UTM String",
              onClick: handleAdd,
              leftIcon: <PlusIcon />,
              disabled:
                utmEntries[NEW_ENTRY_KEY] &&
                utmEntries[NEW_ENTRY_KEY]?.key === "",
            },
          }
        }
        withBackdropClose={!deleteModalIsOpen}
        style={{ overflow: "hidden" }}
      >
        {isEmptyState ? (
          <UtmEmptyState onAdd={handleAdd} />
        ) : (
          <>
            {!isMobile && (
              <UtmEntryContainer>
                <ColumnHeader>UTM String</ColumnHeader>
                <ColumnHeader>Reporting Label</ColumnHeader>
                <ColumnHeader>Instances</ColumnHeader>
                <ColumnHeader> </ColumnHeader>
              </UtmEntryContainer>
            )}
            <Rows>
              {Object.entries(utmEntries).map(
                ([initialKey, { key, label }], index) => (
                  <React.Fragment key={initialKey}>
                    {isMobile && index > 0 && <StyledDivider />}
                    <UtmEntry
                      initialKey={initialKey}
                      entries={utm?.utmSources[initialKey] || 0}
                      sourceKey={key}
                      sourceLabel={label}
                      onChange={handleChange}
                      onDelete={handleDeleteModalOpen}
                      error={entriesErrors[initialKey]}
                    />
                  </React.Fragment>
                ),
              )}
            </Rows>
          </>
        )}
      </Modal>
      <Modal
        header="Delete UTM String?"
        isOpen={deleteModalIsOpen}
        onClose={toggleDeletModalOpen}
        actions={{
          main: {
            text: "Delete",
            onClick: handleDelete,
          },
          secondary: {
            text: "Cancel",
            onClick: toggleDeletModalOpen,
          },
        }}
      >
        <Body1>Are you sure you want to delete this UTM string?</Body1>
      </Modal>
    </>
  );
};

const ColumnHeader = styled.span`
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e2e2;
  height: 25px;
  margin-bottom: 15px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StyledDivider = styled(Divider)`
  margin: 5px -50px 15px;
  width: auto;
  height: 3px;
`;
